import React from 'react';
import { Compass } from 'lucide-react';

// Bússola Viva do Encontro — visualização radar com os dois perfis sobrepostos
// Uso: <BussolaVivaEncontro panorama={enc.panorama} nomeA="Ana" nomeB="Léo" />

const COR_A = '#EC4899';   // rosa
const COR_B = '#38BDF8';   // azul-céu

const BussolaVivaEncontro = ({ panorama, nomeA, nomeB }) => {
  if (!panorama || panorama.length < 4) return null;

  // Posições no círculo (4 estações) - ajustadas para não cortar labels
  const positions = [
    { label: 'Peregrino', angle: -90, key: 'peregrino' },    // topo
    { label: 'Amante', angle: 0, key: 'amante' },            // direita
    { label: 'Alquimista', angle: 90, key: 'alquimista' },   // baixo
    { label: 'Guardião', angle: 180, key: 'guardiao' },      // esquerda
  ];

  const center = 160;  // aumentado para dar mais espaço
  const maxRadius = 90; // reduzido para caber os labels
  const svgSize = 320;  // tamanho total do SVG

  // Converter porcentagem em coordenadas polares
  const getPoint = (pct, angleDeg) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    const r = (pct / 100) * maxRadius;
    return {
      x: center + r * Math.cos(angleRad),
      y: center + r * Math.sin(angleRad),
    };
  };

  // Criar path do polígono para cada pessoa
  const createPolygonPath = (getData) => {
    const points = positions.map(pos => {
      const item = panorama.find(p => p.root === pos.key);
      const pct = item ? getData(item) : 0;
      return getPoint(pct, pos.angle);
    });
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  };

  const pathA = createPolygonPath(item => item.a);
  const pathB = createPolygonPath(item => item.b);

  // Pontos para os marcadores
  const pointsA = positions.map(pos => {
    const item = panorama.find(p => p.root === pos.key);
    return { ...getPoint(item?.a || 0, pos.angle), pct: item?.a || 0 };
  });
  const pointsB = positions.map(pos => {
    const item = panorama.find(p => p.root === pos.key);
    return { ...getPoint(item?.b || 0, pos.angle), pct: item?.b || 0 };
  });

  return (
    <div className="my-8" data-testid="bussola-viva-encontro">
      <div className="flex justify-center mb-4">
        <div className="bg-gradient-to-r from-indigo-900/40 via-purple-900/40 to-indigo-900/40 px-6 py-3 rounded-full border border-indigo-500/20">
          <div className="flex items-center gap-3">
            <Compass className="w-5 h-5 text-indigo-300" />
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-extrabold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bússola Viva do Encontro
              </h2>
              <p className="text-white/50 text-xs mt-0.5">Os dois corações no mesmo mapa</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/10">
        {/* Legenda */}
        <div className="flex items-center justify-center gap-6 mb-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full" style={{ background: COR_A }} />
            <span className="text-white/80">{nomeA}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full" style={{ background: COR_B }} />
            <span className="text-white/80">{nomeB}</span>
          </span>
        </div>

        {/* SVG Radar - com mais padding para labels */}
        <div className="flex justify-center overflow-visible">
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} className="max-w-full" style={{ overflow: 'visible' }}>
            {/* Círculos de referência */}
            {[25, 50, 75, 100].map(pct => (
              <circle
                key={pct}
                cx={center}
                cy={center}
                r={(pct / 100) * maxRadius}
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            ))}

            {/* Linhas dos eixos */}
            {positions.map(pos => {
              const end = getPoint(100, pos.angle);
              return (
                <line
                  key={pos.key}
                  x1={center}
                  y1={center}
                  x2={end.x}
                  y2={end.y}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1"
                />
              );
            })}

            {/* Polígono B (atrás) */}
            <path
              d={pathB}
              fill={`${COR_B}20`}
              stroke={COR_B}
              strokeWidth="2"
              strokeLinejoin="round"
            />

            {/* Polígono A (frente) */}
            <path
              d={pathA}
              fill={`${COR_A}20`}
              stroke={COR_A}
              strokeWidth="2"
              strokeLinejoin="round"
            />

            {/* Pontos B */}
            {pointsB.map((p, i) => (
              <circle
                key={`b-${i}`}
                cx={p.x}
                cy={p.y}
                r="6"
                fill={COR_B}
                stroke="white"
                strokeWidth="2"
              />
            ))}

            {/* Pontos A */}
            {pointsA.map((p, i) => (
              <circle
                key={`a-${i}`}
                cx={p.x}
                cy={p.y}
                r="6"
                fill={COR_A}
                stroke="white"
                strokeWidth="2"
              />
            ))}

            {/* Centro */}
            <circle cx={center} cy={center} r="6" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />

            {/* Labels das estações - posicionados fora do gráfico */}
            {positions.map(pos => {
              const item = panorama.find(p => p.root === pos.key);
              // Posição do label baseada no ângulo
              const labelDist = maxRadius + 35;
              const angleRad = (pos.angle * Math.PI) / 180;
              const lx = center + labelDist * Math.cos(angleRad);
              const ly = center + labelDist * Math.sin(angleRad);
              
              // Ajuste de anchor baseado na posição
              let anchor = 'middle';
              if (pos.angle === 0) anchor = 'start';
              if (pos.angle === 180) anchor = 'end';
              
              return (
                <g key={`label-${pos.key}`}>
                  <text
                    x={lx}
                    y={ly}
                    textAnchor={anchor}
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="13"
                    fontWeight="bold"
                  >
                    {pos.label}
                  </text>
                  <text
                    x={lx}
                    y={ly + 15}
                    textAnchor={anchor}
                    dominantBaseline="middle"
                    fill="rgba(255,255,255,0.5)"
                    fontSize="10"
                  >
                    {item?.a}% / {item?.b}%
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <p className="text-center text-white/50 text-xs mt-4 italic">
          Quanto mais os polígonos se sobrepõem, mais vocês vibram na mesma frequência.
        </p>
      </div>
    </div>
  );
};

export default BussolaVivaEncontro;
